import * as React from 'react';
import { CheckoutOrderFragment, FindGroupOrderMutation, InfoMessageTypeEnum } from '@websky/graphql';
import Passenger from './Passenger/Passenger';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../../../theme';
import cn from 'classnames';
import Toolbar from '../../../../Toolbar';
import WarningModal from '../../../../WarningModal';
import Segment from '../../../../RetrieveBooking/components/Segment/Segment';
import MediaQuery from 'react-responsive';
import { MOBILE_MIN_WIDTH } from '../../../../utils';
import MobileSegment from '../../../../RetrieveBooking/components/MobileSegment/MobileSegment';
import Notification from '../../../../Notification';
import { GroupOrderParamsProps } from '../FindOrder/types';
import { isCorrectCyrillicLastName, isCorrectLatinLastname, isTicketNumber } from '../../../../RetrieveBooking/hooks';

interface Props {
	groupOrder: FindGroupOrderMutation['FindGroupOrder'];
	groupOrderParams: GroupOrderParamsProps;
	onAccept: (passengers: any[]) => Promise<FindGroupOrderMutation['FindGroupOrder']>;
	setGroupOrder: (order: FindGroupOrderMutation['FindGroupOrder']) => void;
	onSuccess: (order: CheckoutOrderFragment) => void;
}

export interface PassengerState {
	id?: string;
	passengerName: string;
	passengerId: string;
	selected?: boolean;
	error?: boolean;
}

const reducer: React.Reducer<PassengerState[], PassengerState> = (state, action) => {
	const isPassengerExists = state.find(passenger => passenger.id === action.id);

	if (isPassengerExists) {
		return state.map(passenger => (passenger.id === action.id ? { ...action } : { ...passenger }));
	} else {
		return [
			...state,
			{
				...action,
				id: state.length.toString()
			}
		];
	}
};

const init = (groupOrderParams: GroupOrderParamsProps) => {
	if (
		isTicketNumber(groupOrderParams.id) &&
		(isCorrectLatinLastname(groupOrderParams.secret) || isCorrectCyrillicLastName(groupOrderParams.secret))
	) {
		return [
			{
				id: '0',
				passengerName: groupOrderParams.secret,
				passengerId: groupOrderParams.id,
				selected: true
			}
		];
	}

	return [];
};

const GroupOrder: React.FC<Props> = props => {
	const [state, dispatch] = React.useReducer(reducer, props.groupOrderParams, init);
	const [loading, setLoading] = React.useState(false);
	const [error, setError] = React.useState(null);

	const { t } = useTranslation('Checkout');
	const theme = useTheme('Checkout').GroupOrder;

	const onChangeHandler = (passenger: PassengerState, selected: boolean) => {
		dispatch({
			...passenger,
			selected
		});
	};

	const onAddPassengerHandler = (lastName: string, id: string, internalId?: string) => {
		return new Promise<boolean>(resolve => {
			dispatch({
				passengerId: id,
				passengerName: lastName,
				selected: true,
				id: internalId
			});

			resolve(true);
		});
	};

	const selectedPassengers = state.filter(passenger => passenger.selected);

	const onNextClickHandler = () => {
		const passengers = selectedPassengers.map(passenger => {
			return {
				id: passenger.id,
				lastName: passenger.passengerName,
				ticketNumber: passenger.passengerId
			};
		});

		if (passengers.length) {
			setLoading(true);

			props
				.onAccept(passengers)
				.then(order => {
					if (order.splitOrder?.id) {
						props.onSuccess(order.splitOrder);
					}

					if (order?.notMatchedTravellersData?.length > 0) {
						order.notMatchedTravellersData.forEach(passenger => {
							dispatch({
								passengerId: passenger.ticketNumber,
								passengerName: passenger.lastName,
								error: true,
								selected: true,
								id: passenger.id
							});
						});
					}

					setLoading(false);
				})
				.catch(errors => {
					setError(errors?.[0]?.message || 'Error while FindGroupOrder');

					setLoading(false);
				});
		}
	};

	return (
		<div className={theme.groupOrder}>
			<div className={theme.header}>
				<div className={theme.container}>{t('Search my booking')}</div>
			</div>

			<div className={cn(theme.container, theme.notifications)}>
				<Notification
					className={theme.notification__item}
					status={InfoMessageTypeEnum.Warning}
					type="context"
					infoMessage={{
						text: t('Specify all passengers for whom you need to open an order for correct display.'),
						type: InfoMessageTypeEnum.Warning,
						code: 'warning'
					}}
				/>
			</div>

			{props.groupOrder.flight?.segmentGroups?.length > 0 && (
				<div className={cn(theme.container, theme.flight)}>
					{props.groupOrder.flight.segmentGroups.map((segment, index) => (
						<React.Fragment key={segment.groupId}>
							<MediaQuery minWidth={MOBILE_MIN_WIDTH}>
								<Segment variant={'common'} segment={segment} />
							</MediaQuery>

							<MediaQuery maxWidth={MOBILE_MIN_WIDTH}>
								<MobileSegment segment={segment} className={theme.segment} />
							</MediaQuery>
						</React.Fragment>
					))}
				</div>
			)}

			<div className={cn(theme.container, theme.passengers)}>
				{state.map(passenger => {
					return (
						<Passenger
							passenger={passenger}
							key={passenger.passengerId}
							onSelect={onChangeHandler}
							onAddPassenger={onAddPassengerHandler}
						/>
					);
				})}

				<Passenger type="add" isOpen={!state.length} onAddPassenger={onAddPassengerHandler} />
			</div>

			<Toolbar
				totalPrice={null}
				isLoading={loading}
				buttonDisabled={!selectedPassengers.length}
				onButtonClick={onNextClickHandler}
			/>

			<WarningModal
				isOpen={!!error}
				onClose={() => setError(null)}
				title={t('Oops..')}
				content={t(
					'An unexpected error has occurred during the tickets downloading process. Please try again later or contact a customer support service.'
				)}
				errorMessage={error}
			/>
		</div>
	);
};

export default GroupOrder;
